import { type FC, useState, useEffect, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Textarea,
} from '@/components/ui';
import { useAuth } from '@/hooks';
import { supabase } from '@/lib/api/supabase';
import { formatPrice, formatTime } from '@/lib/utils/formatting';
import { sendActivityRegistrationEmail } from '@/lib/utils/email';
import type { Activity } from '@/types';
import { dbToActivity } from '@/types';
import { ArrowLeft, Calendar } from 'lucide-react';

/**
 * Activity registration page
 */
export const ActivityRegistrationPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [club, setClub] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { returnTo: `/activities/${id}/register` } });
    }
  }, [authLoading, user, navigate, id]);

  // Fetch activity details
  useEffect(() => {
    const fetchActivity = async (): Promise<void> => {
      if (!id) return;

      try {
        setLoading(true);

        const { data, error: fetchError } = await supabase
          .from('activities')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (data) {
          setActivity(dbToActivity(data));
        }
      } catch (err) {
        console.error('Failed to load activity:', err);
        setError(err instanceof Error ? err.message : 'Failed to load activity details');
      } finally {
        setLoading(false);
      }
    };

    void fetchActivity();
  }, [id]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!user || !activity) return;

    setError(null);
    setSubmitting(true);

    try {
      // Check if already registered
      const { data: existing } = await supabase
        .from('activity_registrations')
        .select('id')
        .eq('user_id', user.id)
        .eq('activity_id', activity.id)
        .single();

      if (existing) {
        setError('You are already registered for this activity');
        setSubmitting(false);
        return;
      }

      // Check if activity is full
      if (activity.maxParticipants && activity.currentParticipants >= activity.maxParticipants) {
        setError('This activity is full');
        setSubmitting(false);
        return;
      }

      // Create registration
      const { error: insertError } = await supabase.from('activity_registrations').insert([
        {
          user_id: user.id,
          activity_id: activity.id,
          club: club || null,
          special_requests: specialRequests || null,
          payment_status: activity.fee > 0 ? 'pending' : 'completed',
          amount_paid: activity.fee,
        },
      ]);

      if (insertError) throw insertError;

      // Update participant count
      const { error: updateError } = await supabase
        .from('activities')
        .update({
          current_participants: activity.currentParticipants + 1,
        })
        .eq('id', activity.id);

      if (updateError) throw updateError;

      // Send activity registration confirmation email
      try {
        // Get user profile for name
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();

        const userName = profile?.first_name && profile?.last_name
          ? `${profile.first_name} ${profile.last_name}`
          : profile?.first_name || 'Fighter';

        await sendActivityRegistrationEmail(
          user.email || '',
          userName,
          activity.title,
          activity.date,
          activity.startTime ? formatTime(activity.startTime) : undefined,
          activity.description || undefined
        );
        console.log('Activity registration email sent to:', user.email);
      } catch (emailError) {
        // Don't fail registration if email fails
        console.error('Failed to send activity registration email:', emailError);
      }

      // Navigate to dashboard with success message
      navigate('/dashboard', {
        state: { message: `Successfully registered for ${activity.title}!` },
      });
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">Activity not found</p>
            <Button onClick={() => navigate('/activities')} className="mt-4">
              Back to Activities
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isFree = activity.fee === 0;

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/activities')} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Activities
        </Button>

        {/* Activity info */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start gap-4">
              <Calendar className="h-8 w-8 text-primary" />
              <div className="flex-1">
                <CardTitle className="text-2xl font-viking">{activity.title}</CardTitle>
                <CardDescription className="mt-2">
                  {activity.type} • {activity.instructor} • {formatTime(activity.startTime)} ({activity.duration} min)
                  {!isFree && ` • ${formatPrice(activity.fee)}`}
                </CardDescription>
                {activity.description && (
                  <p className="text-sm text-muted-foreground mt-3">{activity.description}</p>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Registration form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-viking">Registration</CardTitle>
            <CardDescription>
              {isFree
                ? 'Reserve your spot for this free activity'
                : 'Complete your registration below'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md border border-destructive/20">
                  {error}
                </div>
              )}

              {/* Participant info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Registered email cannot be changed
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="club">Club/School (Optional)</Label>
                  <Input
                    id="club"
                    value={club}
                    onChange={(e) => setClub(e.target.value)}
                    placeholder="Your HEMA club or school"
                    disabled={submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specialRequests">Special Requests or Notes (Optional)</Label>
                  <Textarea
                    id="specialRequests"
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="Any questions, requests, or information for the instructor"
                    disabled={submitting}
                    rows={3}
                  />
                </div>
              </div>

              {/* Activity details summary */}
              <div className="bg-muted p-4 rounded-md space-y-2">
                <h4 className="font-medium">Activity Details</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>
                    <span className="font-medium text-foreground">Date:</span> {activity.date}
                  </p>
                  <p>
                    <span className="font-medium text-foreground">Time:</span>{' '}
                    {formatTime(activity.startTime)}
                  </p>
                  <p>
                    <span className="font-medium text-foreground">Duration:</span>{' '}
                    {activity.duration} minutes
                  </p>
                  <p>
                    <span className="font-medium text-foreground">Skill Level:</span>{' '}
                    {activity.skillLevel}
                  </p>
                  {activity.maxParticipants && (
                    <p>
                      <span className="font-medium text-foreground">Spots Available:</span>{' '}
                      {activity.maxParticipants - activity.currentParticipants} /{' '}
                      {activity.maxParticipants}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="flex items-center justify-between pt-4 border-t">
                {!isFree ? (
                  <div>
                    <p className="font-medium">Registration Fee</p>
                    <p className="text-2xl font-bold text-primary">{formatPrice(activity.fee)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-primary">Free Activity</p>
                    <p className="text-sm text-muted-foreground">No payment required</p>
                  </div>
                )}
                <Button type="submit" size="lg" disabled={submitting}>
                  {submitting
                    ? 'Processing...'
                    : isFree
                      ? 'Confirm Registration'
                      : 'Proceed to Payment'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
